<template>
    <div class="wrapper">
        <div class="search">
            <SearchForm
                @set-position="this.setPosition"
                @set-marker="this.setMarker"
                @clear-query="this.clearQuery"
                @highlight-schools="this.highlightSchools"
            />
        </div>

        <div class="app" :class="{ showFilter, showList }">
            <Map>
                <View
                    ref="view"
                    :position="position"
                    :padding="padding"
                    @position-changed="onPositionChanged"
                />
                <SelectInteraction :selected="selectedFeature" @select="onSelectFeature" />
                <WMTSLayer
                    name="achtergrondkaart"
                    url="https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:28992/{z}/{x}/{y}.png"
                    layer="brtachtergrondkaartgrijs"
                />
                <VectorLayer
                    name="schools"
                    styleName="school"
                    :features="displaySchools"
                    :selectable="true"
                />
                <VectorLayer
                    name="home"
                    v-if="this.marker"
                    :features="[{ geometry: { type: 'Point', coordinates: this.marker } }]"
                    styleName="home"
                    :isMarker="true"
                    :selectable="false"
                />
            </Map>

            <div class="toggle-buttons">
                <div class="filter-button" v-if="!this.showFilter">
                    <Button size="large" label="Verfijn" dropShadow @click="toggleFilter">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24px"
                            viewBox="0 0 24 24"
                            width="24px"
                            fill="#000000"
                        >
                            <path d="M0 0h24v24H0V0z" fill="none" />
                            <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                        </svg>
                    </Button>
                    <div v-if="activeFilterCount > 0" class="filter-count">
                        {{ activeFilterCount }}
                    </div>
                </div>
                <Button
                    v-if="!this.showList"
                    size="large"
                    label="Lijst"
                    dropShadow
                    @click="toggleList"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        enable-background="new 0 0 24 24"
                        height="24px"
                        viewBox="0 0 24 24"
                        width="24px"
                        fill="#000000"
                    >
                        <rect fill="none" height="24" width="24" />
                        <path
                            d="M3,5v14h18V5H3z M7,7v2H5V7H7z M5,13v-2h2v2H5z M5,15h2v2H5V15z M19,17H9v-2h10V17z M19,13H9v-2h10V13z M19,9H9V7h10V9z"
                        />
                    </svg>
                </Button>
            </div>

            <ListPanel
                ref="listPanel"
                v-if="showList && !selectedFeature"
                class="list-panel"
                :marker="
                    this.marker ? { geometry: { type: 'Point', coordinates: this.marker } } : null
                "
                :schools="displaySchools"
                @hidePanel="showList = false"
                @select="onSelectFeature"
            />

            <DetailPanel
                ref="detailPanel"
                v-if="selectedFeature"
                :feature="selectedFeature"
                @hideDetailAndListPanel="hideDetailAndListPanel"
                @hideDetailPanel="clearSelectedFeature"
            />

            <FilterPanel
                v-if="showFilter"
                ref="filterPanel"
                :facets="facets"
                v-model="filters"
                class="filter-panel"
                @hidePanel="hideFilterPanel"
            />

            <ZoomPanel :position="position" @adjust-zoom="adjustZoom" />
        </div>
    </div>
</template>

<script>
import GeoJSON from 'ol/format/GeoJSON'
import { boundingExtent } from 'ol/extent'

import SearchForm from './components/SearchForm.vue'
import Map from './components/Map/Map.vue'
import View from './components/Map/View.vue'
import Button from './components/Button.vue'
import SelectInteraction from './components/Map/SelectInteraction.vue'
import WMTSLayer from './components/Map/WMTSLayer.vue'
import VectorLayer from './components/Map/VectorLayer.vue'
import ListPanel from './components/ListPanel.vue'
import DetailPanel from './components/DetailPanel.vue'
import FilterPanel from './components/FilterPanel.vue'
import ZoomPanel from './components/Map/ZoomPanel.vue'
import { getSchools } from './services'

const geoJSON = new GeoJSON()

export default {
    name: 'App',
    components: {
        SearchForm,
        Map,
        View,
        Button,
        SelectInteraction,
        WMTSLayer,
        VectorLayer,
        ListPanel,
        DetailPanel,
        FilterPanel,
        ZoomPanel,
    },
    async mounted() {
        this.allSchools = await getSchools()

        const geometries = this.displaySchools.map((school) =>
            geoJSON.readGeometry(school.geometry).getCoordinates()
        )

        this.$refs.view.fit(boundingExtent(geometries), {
            padding: this.padding,
        })
    },
    data() {
        return {
            position: { zoom: 13, center: [133587, 455921] },
            padding: [25, 25, 25, 25],
            marker: null,
            allSchools: [],
            highlightedSchools: [],
            filters: {},
            selectedFeature: null,
            showFilter: false,
            showList: false,
        }
    },
    created() {
        window.addEventListener('resize', this.onResizeWindow)
    },
    destroyed() {
        window.removeEventListener('resize', this.onResizeWindow)
    },
    computed: {
        displaySchools() {
            return this.allSchools
                .map((school) => {
                    return {
                        ...school,
                        highlighted: this.highlightedSchools.includes(school.id),
                    }
                })
                .filter((school) => {
                    let display = true
                    Object.keys(this.filters).forEach((filterKey) => {
                        if (
                            this.filters[filterKey].length > 0 &&
                            !this.filters[filterKey].includes(school[filterKey])
                        ) {
                            display = false
                        }
                    })

                    return display
                })
        },
        activeFilterCount() {
            let activeFilterCount = 0

            Object.keys(this.filters).forEach((filterKey) => {
                activeFilterCount += this.filters[filterKey].length
            })

            return activeFilterCount
        },
        facets() {
            const facets = [
                {
                    title: 'Onderwijsvorm',
                    key: 'onderwijsvorm',
                },
                {
                    title: 'Identiteit',
                    key: 'identiteit',
                },
                {
                    title: 'Stedelijke school',
                    key: 'stedelijke_school',
                },
            ]

            facets.forEach((facet) => {
                facet.values = [
                    ...new Set(this.allSchools.map((school) => school[facet.key])),
                ].sort()
            })

            return facets
        },
    },
    methods: {
        setPosition(position) {
            this.$refs.view.centerOn(position, {
                padding: this.padding,
                duration: 250,
            })
        },
        adjustZoom(delta, options) {
            this.$refs.view.adjustZoom(delta, options)
        },
        onPositionChanged(position) {
            this.position = position
        },
        setMarker(marker) {
            this.marker = marker
        },
        onSelectFeature(feature) {
            this.selectedFeature = feature

            if (feature !== null) {
                this.showList = true

                this.$nextTick(() => {
                    this.$nextTick(() => {
                        this.$refs.view.centerOn(feature.geometry.coordinates, {
                            padding: this.padding,
                            duration: 250,
                        })
                    })
                })
            }

            window.top.postMessage('atlas:event:select-feature', '*')
        },
        clearSelectedFeature() {
            this.selectedFeature = null
        },
        clearQuery() {
            this.marker = null
            this.highlightedSchools = []
        },
        hideDetailAndListPanel() {
            this.showList = false
            this.selectedFeature = null
        },
        hideFilterPanel() {
            this.showFilter = false
        },
        highlightSchools(highlightedSchools, marker) {
            this.highlightedSchools = highlightedSchools
            this.filters = {}
            this.showList = true

            const geometries = this.displaySchools
                .filter((school) => school.highlighted)
                .map((school) => geoJSON.readGeometry(school.geometry).getCoordinates())

            geometries.push(marker)

            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.$refs.view.fit(boundingExtent(geometries), {
                        padding: this.padding,
                        duration: 250,
                    })
                })
            })
        },
        toggleFilter() {
            if (document.body.clientWidth < 1024) {
                if (!this.showFilter) {
                    this.showList = false
                    this.clearSelectedFeature()
                }
            }
            this.showFilter = !this.showFilter

            if (this.showFilter) {
                window.top.postMessage('atlas:event:show-filter', '*')
            }
        },
        toggleList() {
            if (document.body.clientWidth < 1024) {
                if (!this.showList) {
                    this.showFilter = false
                    this.clearSelectedFeature()
                }
            }
            this.showList = !this.showList

            if (this.showList) {
                window.top.postMessage('atlas:event:show-list', '*')
            }
        },
        updatePadding() {
            let top = 25
            let right = 25
            let bottom = 25
            let left = 25

            if (window.innerWidth <= 576) {
                if (this.$refs.listPanel) {
                    bottom += this.$refs.listPanel.$el.clientHeight
                } else if (this.$refs.detailPanel) {
                    bottom += this.$refs.detailPanel.$el.clientHeight
                } else if (this.$refs.filterPanel) {
                    bottom += this.$refs.filterPanel.$el.clientHeight
                }
            } else {
                if (this.$refs.listPanel) {
                    left += this.$refs.listPanel.$el.clientWidth
                } else if (this.$refs.detailPanel) {
                    left += this.$refs.detailPanel.$el.clientWidth
                }

                if (this.$refs.filterPanel) {
                    left += this.$refs.filterPanel.$el.clientWidth
                }
            }

            this.padding = [top, right, bottom, left]
        },
        onResizeWindow() {
            this.updatePadding()
        },
    },
    watch: {
        filters(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.clearSelectedFeature()
            }
        },
        showFilter() {
            this.$nextTick(() => {
                this.updatePadding()
            })
        },
        async showList() {
            this.$nextTick(() => {
                this.updatePadding()
            })
        },
    },
}
</script>

<style>
:root {
    --font-size-tiny: 12px;
    --font-size-small: 14px;
    --font-size-normal: 16px;
    --font-size-large: 18px;

    --font-weight-normal: 400;
    --font-weight-bold: 700;

    --color-primary: #24578f;

    --color-text-grey: rgba(0, 0, 0, 0.55);

    --color-hover: rgba(0, 0, 0, 0.03);
    --color-active: rgba(0, 0, 0, 0.06);

    --color-grey-10: #f5f5f5; /* background */
    --color-grey-20: #eaeaea; /* divider (list items, files) */
    --color-grey-30: #dddddd; /* divider (sections, header) */
    --color-grey-40: #bfbfbf; /* button border, tag border */
    --color-grey-50: #949494; /* input border (text, checkbox, switch, etc.) */

    --color-icon-grey: rgba(0, 0, 0, 0.42);

    --color-tooltip-dark: #222222;

    --radius-small: 4px;
    --radius-normal: 6px;
    --shadow-normal: 0 0 1px rgba(0, 0, 0, 0.2), 0 0 8px rgba(0, 0, 0, 0.15);

    --padding-screen: 20px;
    --panel-width: 100%;
}

@media (min-width: 576px) {
    :root {
        --panel-width: 320px;
    }
}

/* @media (min-width: 768px) {
    :root {
    }
}

@media (min-width: 1200px) {
    :root {
    }
} */

html,
body {
    width: 100%;
    height: 100%;
}

html {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
        'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
    font-size: var(--font-size-normal);
    font-weight: var(--font-weight-normal);
}

body {
    margin: 0;
    word-break: break-word;
}

button {
    text-align: left;
    font: inherit;
}

button:not([disabled]) {
    cursor: pointer;
}

ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
}

#app {
    width: 100%;
    height: 100%;
}

.tippy-box {
    padding: 0;
    border-radius: var(--radius-normal);
    font-family: inherit;
    font-size: var(--font-size-tiny);
    font-weight: var(--font-weight-bold);
    letter-spacing: 0em;
    color: white;
}

.tippy-box .tippy-content {
    padding: 3px 7px 4px;
}

.tippy-box[data-theme~='dark'] {
    background-color: var(--color-tooltip-dark);
}

.tippy-box[data-theme~='popover'] {
    background-color: white;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-normal);
    color: #000000;
    letter-spacing: inherit;
    box-shadow: var(--shadow-normal);
}

.tippy-box[data-placement^='left'] .tippy-arrow:before {
    border-left-color: white;
}

.tippy-box[data-placement^='right'] .tippy-arrow:before {
    border-right-color: white;
}

.tippy-box[data-theme~='popover'] .tippy-content {
    padding: 0;
    overflow: auto;
}
</style>

<style scoped>
.wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.content {
    padding: 24px;
    border-bottom: 1px solid var(--color-grey-30);
}

.content h2 {
    margin: 0;
}

.app {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    overflow: hidden;
}

.search {
    padding: var(--padding-screen);
    box-shadow: var(--shadow-normal);
    z-index: 1;
}

.panel {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: var(--panel-width);
}

@media (max-width: 575px) {
    .panel {
        height: 70%;
    }

    .panel.expand {
        height: 100%;
    }

    .app.showFilter .zoom-panel,
    .app.showList .zoom-panel {
        bottom: calc(70% + var(--padding-screen));
    }
}

@media (min-width: 576px) {
    .panel {
        height: 100%;
    }

    .app.showFilter .list-panel,
    .app.showFilter .detail-panel {
        left: var(--panel-width);
    }

    .app.showFilter .toggle-buttons,
    .app.showList .toggle-buttons {
        left: calc(var(--padding-screen) + var(--panel-width));
    }

    .app.showFilter.showList .toggle-buttons {
        left: calc(var(--padding-screen) + var(--panel-width) * 2);
    }

    .app.showList .filter-panel {
        box-shadow: 1px 0 0 var(--color-grey-30);
    }
}

.zoom-panel {
    position: absolute;
    right: var(--padding-screen);
    bottom: var(--padding-screen);
}

.content {
    flex: 1 1 0%;
    position: relative;
}

.map {
    width: 100%;
    height: 100%;
}

.toggle-buttons {
    position: absolute;
    top: var(--padding-screen);
    left: var(--padding-screen);
    display: flex;
}

.toggle-buttons > *:not(:last-child) {
    margin-right: 8px;
}

.filter-button {
    display: flex;
}

.filter-count {
    position: relative;
    flex-shrink: 0;
    margin-top: 2px;
    margin-left: -9px;
    box-sizing: border-box;
    height: 18px;
    min-width: 18px;
    border-radius: 9px;
    border: 2px solid var(--color-primary);
    padding: 0 3px;
    background: white;
    color: var(--color-primary);
    font-size: 11px;
    font-weight: var(--font-weight-bold);
    line-height: 14px;
    text-align: center;
    white-space: nowrap;
    user-select: none;
}
</style>
